import { difference, omitBy, mapValues, sample, size, map, sortBy, keys, reduce } from 'lodash';
import { database } from './';
import { clearStrokes } from './stroke';
import { Room, UserRoomLink, GameStatus, User } from '../types';
import { getUserRoomLinksForRoomId } from './queries';
import { createAdminMessage } from './message';

export function createRoom() {
    const id = database.ref().child('rooms').push().key;

    if (!id) {
        return Promise.reject();
    }

    const room: Room = {
        id,
        gameStatus: GameStatus.WaitingToStartGame,
        round: 1,
    };
    return database
        .ref(`rooms/${id}`)
        .set(room)
        .then(() => room);
}

export function getRoomRef(roomId: string) {
    return database.ref('rooms').child(roomId);
}

export const roundLength = 1000 * 60;

const maxRounds = 3;

export function startNextRound(roomId: string, round: number) {
    return getUserRoomLinksForRoomId(roomId).once('value', (dataSnapshot) => {
        const userRoomLinks: UserRoomLink[] = dataSnapshot.val();
        const usersLeftInRound = reduce(
            map(userRoomLinks, 'userId'),
            (acc: any, userId: string) => {
                acc[userId] = true;
                return acc;
            },
            {},
        );

        const startingUserId = sample(keys(usersLeftInRound));

        return database
            .ref('rooms')
            .child(roomId)
            .update({
                gameStatus: GameStatus.WaitingToStartNextDrawer,
                round,
                roundEndTime: null,
                drawer: startingUserId,
                answer: null,
                // Pick someone to be the "host"
                primaryUserId: startingUserId,
                usersLeftInRound,

                // reset the scores if we are starting at round 1
                ...(round === 1 ? { scores: {} } : {}),
            });
    });
}

export const maxRoundScore = 500;
export const penalty = 10;
export function endDrawer(roomId: string) {
    return database
        .ref('rooms')
        .child(roomId)
        .once('value', (dataSnapshot) => {
            const { correctUsers, scores, usersLeftInRound, round } = dataSnapshot.val();

            // Omit the current drawer, and also any non scores
            const sortedCorrectUserIds = sortBy(
                keys(correctUsers),
                (userId) => correctUsers[userId],
            );

            const updatedScores = reduce(
                sortedCorrectUserIds,
                (acc, userId, idx) => {
                    // If this user has a score,
                    // then add them together
                    if (!acc[userId]) {
                        acc[userId] = 0;
                    }
                    acc[userId] += maxRoundScore - penalty * idx;
                    return acc;
                },
                scores || {},
            );

            const usersLeftToDraw = omitBy(
                usersLeftInRound,
                (userValue: any) => userValue === false,
            );

            // If all the people have drawn already
            if (!size(usersLeftToDraw)) {
                // If we are at the max rounds, then end the game
                if (round >= maxRounds) {
                    return database.ref('rooms').child(roomId).update({
                        gameStatus: GameStatus.GameOver,
                        scores: updatedScores,
                        correctUsers: {},
                    });
                }
                // Else, start a new round
                else {
                    database
                        .ref('rooms')
                        .child(roomId)
                        .update({
                            scores: updatedScores,
                        })
                        .then(() => {
                            return startNextRound(roomId, round + 1);
                        });
                }
            }
            // queue the next player
            else {
                const startingUserId = sample(keys(usersLeftToDraw));

                return database.ref('rooms').child(roomId).update({
                    gameStatus: GameStatus.WaitingToStartNextDrawer,
                    roundEndTime: null,
                    drawer: startingUserId,
                    scores: updatedScores,
                });
            }
        });
}

export function userGuessedCorrectly(roomId: string, userId: string) {
    return database
        .ref('users')
        .child(userId)
        .once('value', (dataSnapshot) => {
            const user: User = dataSnapshot.val();
            createAdminMessage(roomId, `${user.name} got it`);

            // Set the correct user
            return database
                .ref('rooms')
                .child(roomId)
                .child('correctUsers')
                .child(userId)
                .set(new Date().getTime())
                .then(() => {
                    // After we set, if there is only one more correct user
                    // then end the round
                    return database
                        .ref('rooms')
                        .child(roomId)
                        .once('value', (dataSnapshot) => {
                            const { correctUsers, usersLeftInRound } = dataSnapshot.val();
                            const remainingUsers = difference(
                                keys(usersLeftInRound),
                                keys(correctUsers),
                            );
                            if (size(remainingUsers) === 1) {
                                return endDrawer(roomId);
                            }
                        });
                });
        });
}

export function startNextDrawer(roomId: string, word: string) {
    clearStrokes(roomId);
    return database
        .ref('rooms')
        .child(roomId)
        .once('value', (dataSnapshot) => {
            const { usersLeftInRound, drawer } = dataSnapshot.val();

            // Begin the next round
            const remainingUsers = mapValues(usersLeftInRound, (val, userId) => {
                if (userId === drawer) {
                    return false;
                } else {
                    return val;
                }
            });

            return database
                .ref('rooms')
                .child(roomId)
                .update({
                    gameStatus: GameStatus.RoundInProgress,
                    roundEndTime: new Date().getTime() + roundLength,
                    answer: word,
                    usersLeftInRound: remainingUsers,
                    correctUsers: {},
                });
        });
}
